import { SortDescriptor, filterBy, groupBy, orderBy } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { Icon } from "@progress/kendo-react-common";
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';
import { Grid, GridCellProps, GridColumn, GridNoRecords, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { InputSuffix, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Breadcrumb, DataModel } from '@progress/kendo-react-layout';
import { ListView, ListViewItemProps } from '@progress/kendo-react-listview';
import { useCallback, useEffect, useMemo, useState } from "react";
import LoaderContainer from '../commons/container/LoaderContainer';
import { calculateWidth } from "../commons/helpers/GridHelper";
import { clone } from '../commons/helpers/ObjectHelper';
import CustomModal from '../commons/modals/CustomModal';
import { Activite } from '../models/Activite';
import { Categorie } from '../models/Categorie';
import { Departement } from '../models/Departement';
import { Division } from '../models/Division';
import { Fournisseur } from '../models/Fournisseur';
import { Tache } from '../models/Tache';
import { useGetProduitsQuery, useLazyGetCategoriesQuery, useLazyGetDepartementsQuery, useLazyGetProduitsQuery } from '../redux/api/cache';
import { useAppSelector } from '../redux/store';
import ActionsCell from './grid/ActionsCell';
import { columns } from "./grid/Columns";
import Toolbar from "./grid/Toolbar";
import CustomListView from './listview/CustomListView';
import { listViewFields } from './listview/Fields';
import "./mainlayout.css";
import Instructions from './tasks/Instructions';
import TaskForm from './tasks/TaskForm';
import TreeViewFilters from './treeview/TreeViewFilters';

export default function MainLayout() {
    const pageSizes = [10, 20, 50, 75];
    let buttonsState = useAppSelector(state => state.appState.buttonsState);
    let d_inv010 = useAppSelector(state => state.appState.d_inv010);
    const [fournisseur, setFournisseur] = useState<Fournisseur>({} as Fournisseur);
    const [categorie, setCategorie] = useState<Categorie>({} as Categorie);
    const [departement, setDepartement] = useState<Departement>({} as Departement);
    const [division, setDivision] = useState<Division>({ div_num: -1 } as Division);
    const [tache, setTache] = useState<Tache>({ id: -1 } as Tache);
    const [activite, setActivite] = useState<Activite>({ act_cd: -1 } as Activite);
    const [pageSize, setPageSize] = useState<number>(10);
    const [taches, setTaches] = useState<string>('');
    const [acheteur, setAcheteur] = useState<string>('');
    const [approuveFrancais, setApprouveFrancais] = useState<boolean | null>(null);
    const [approuveAnglais, setApprouveAnglais] = useState<boolean | null>(null);
    const [page, setPage] = useState<number>(1);
    const [searchProductValue, setSearchProductValue] = useState<string>("");
    const [searchMode, setSearchMode] = useState(false);
    const { data: produits = { data: [], totalCount: 0 }, isFetching, isLoading } = useGetProduitsQuery({
        four: fournisseur.four_cd, cat: categorie.cat_cd,
        dept: departement.dept_cd, div: division.div_num, page, pagesize: pageSize, taches: tache.id === -1 && !searchMode ? taches : "", tacheId: tache.id, ptr: "", acht: acheteur,
        approuveang: approuveAnglais, approuvefra: approuveFrancais, activite: activite.act_cd
    }, {
        skip: !fournisseur.four_cd && !categorie.cat_cd && !departement.dept_cd && division.div_num === -1 && tache.id === -1 && activite.act_cd === -1, refetchOnMountOrArgChange: true
    });
    const [filteredData, setFilteredData] = useState<any[]>(clone(produits.data));
    const [total, setTotal] = useState<number>(produits.totalCount);
    const [breadCrumbData, setBreadCrumbData] = useState<DataModel[]>([]);
    const [searchField, setSearchField] = useState<string>("");
    const [sort, setSort] = useState<SortDescriptor[]>([{ field: "" }]);
    const [getCategories] = useLazyGetCategoriesQuery();
    const [getDepartements] = useLazyGetDepartementsQuery();
    const [getProduits] = useLazyGetProduitsQuery();
    const fournisseursChildren = [{ update: (fournisseur: Fournisseur = {} as Fournisseur) => setFournisseur(fournisseur), fetch: getCategories }, { update: (categorie: Categorie = {} as Categorie) => setCategorie(categorie) }];
    const divisionsChildren = [{ update: (division: Division = { div_num: -1 } as Division) => setDivision(division), fetch: getDepartements }, { update: (dept: Departement = {} as Departement) => setDepartement(dept), fetch: getCategories }, { update: (categorie: Categorie = {} as Categorie) => setCategorie(categorie) }];
    const tachesChildren = [{ update: (tache: Tache = { id: -1 } as Tache) => setTache(tache) }];
    const activitesChildren = [{ update: (activite: Activite = { act_cd: -1 } as Activite) => setActivite(activite) }];
    const [treeViewKey, setTreeViewKey] = useState<number>(0);
    const handleChange = useCallback((event: TextBoxChangeEvent) => {
        setSearchProductValue(event.target.value as string);
    }, []);
    const [openModalNewTask, setOpenModalNewTask] = useState<boolean>(false);
    const [instructionsExpanded, setInstructionsExpanded] = useState<boolean>(false);

    const resetAllQuery = () => {
        setSearchProductValue("")
        setSearchMode(false);
        setSearchField("");
        setPage(1);
        fournisseursChildren.forEach(f => f.update());
        divisionsChildren.forEach(f => f.update());
        tachesChildren.forEach(f => f.update());
        activitesChildren.forEach(f => f.update());
    };

    useEffect(() => {
        if (!searchMode && (produits.data.length !== 0 || (produits.data.length === 0 && filteredData.length > 0))) {
            setFilteredData(clone(produits.data));
            setTotal(produits.totalCount);
        }
    }, [produits]);

    useEffect(() => {
        let result = [] as DataModel[];
        if (fournisseur.four_cd || categorie.cat_cd || departement.dept_cd || division.div_num !== -1 || tache.id !== -1 || activite.act_cd !== -1) {
            if (fournisseur.four_cd) {
                result.push({ text: fournisseur.four_nom_aff, id: fournisseur.four_cd });
                if (categorie.cat_cd)
                    result.push({ text: categorie.cat_descr_aff, id: categorie.cat_cd });
            } else if (division.div_num !== -1) {
                result.push({ text: division.div_nom_aff, id: division.div_num.toString() });
                if (departement.dept_cd) {
                    result.push({ text: departement.dept_nom_aff, id: departement.dept_cd });
                    if (categorie.cat_cd)
                        result.push({ text: categorie.cat_descr_aff, id: categorie.cat_cd });
                }
            } else if (tache.id !== -1) {
                result.push({ text: tache.description, id: tache.id.toString() });
            } else if (activite.act_cd !== -1) {
                result.push({ text: activite.act_descr, id: activite.act_cd.toString() })
            }
            setBreadCrumbData(result);
        } else {
            if (searchProductValue && filteredData.length === 0) {
                setBreadCrumbData(result);
            }
            if (searchProductValue && filteredData.length === 1) {
                result.push({ text: searchProductValue, id: searchProductValue })
                setBreadCrumbData(result);
            }
        }

    }, [fournisseur, categorie, departement, division, tache, activite, filteredData])

    const onSearchChange = (value: any) => {
        setSearchField(value.target.value);
        let headers = buttonsState.gridView ? columns : listViewFields;
        let filters = headers.filter(h => h.canInteract).map((item) => {
            return {
                field: item.field,
                operator: 'contains',
                value: value.target.value.toString(),
                ignoreCase: true
            }
        });
        setFilteredData(filterBy(clone(produits.data).map((produit: any) => {
            produit.approuve_web = produit.approuve_web ? "Oui" : "Non";
            return produit;
        }), {
            logic: 'or',
            filters: filters
        }))
    };

    const updateData = useCallback((data: any) => {
        let produit = filteredData.find(f => f.prod_num === data.prod_num);
        Object.assign(produit, data);
    }, [filteredData])

    const deleteData = useCallback((prod_num: number) => {
        let newData = filteredData.filter(f => f.prod_num !== prod_num);
        setFilteredData(newData);
    }, [filteredData]);

    const CustomListviewWrapper = useCallback((props: ListViewItemProps) => {
        return <CustomListView defaultProps={props} updateData={updateData} deleteData={deleteData} idTache={tache.id} />
    }, [tache, deleteData, updateData]);

    const ActionsCellWrapper = (props: GridCellProps) => {
        return <ActionsCell defaultProps={props} updateData={updateData} deleteData={() => { }} />;
    }

    const resetData = (tacheId: number) => {
        if (tacheId === tache.id) {
            setFilteredData([]);
            setTache({ id: -1 } as Tache);
        }
    }

    const fetchProduct = async () => {
        setCategorie({} as Categorie);
        setDepartement({} as Departement);
        setDivision({ div_num: -1 } as Division);
        setFournisseur({} as Fournisseur);
        setTache({ id: -1 } as Tache)
        setTreeViewKey(key => key + 1);
        setBreadCrumbData([]);
        setSearchMode(true);
        let result = await getProduits({ ptr: searchProductValue, div: -1, cat: "", dept: "", four: "", page: 1, pagesize: 20, taches: "", tacheId: -1, acht: "", approuveang: null, approuvefra: null, activite: -1 });
        setFilteredData(clone(result.data.data));
        setTotal(result.data.totalCount);
    }

    const listViewData = useMemo(() => {
        return groupBy(filteredData, [{ field: "prod_groupe" }])
    }, [filteredData]);

    return <>
        <div className='k-flex-grow k-h-full k-max-h-full k-min-h-0'>
            <div className="k-d-flex k-h-full">
                <div className="panneau-gauche k-d-flex k-flex-column k-grid-cols-1 k-h-full k-gap-1">
                    <TextBox value={searchProductValue} onChange={handleChange} placeholder="Rechercher un produit (#PTR)" suffix={() => (<InputSuffix><Icon name="search" style={{ cursor: 'pointer' }} onClick={fetchProduct} /></InputSuffix>)} className='global-search-input' onKeyDown={(e) => {
                        if (e.key === "Enter")
                            fetchProduct()
                    }} />
                    <TreeViewFilters resetAllQuery={resetAllQuery} divisionsChildren={divisionsChildren} fournisseursChildren={fournisseursChildren} tachesChildren={tachesChildren} activitesChildren={activitesChildren} key={treeViewKey} resetData={resetData} />
                </div>
                <div className='k-flex-grow k-d-flex k-flex-column k-grid-cols-1 k-h-full k-p-md'>
                    <div className="k-align-items-center k-flex-shrink-0 k-d-flex k-flex-row k-grid-rows-1 k-gap-1"><Breadcrumb data={breadCrumbData} disabled={true} className='k-flex-grow' /><Button className='blueButton' disabled={filteredData.length === 0 || !d_inv010.canModify} onClick={() => { setOpenModalNewTask(true) }}>Créer une tâche</Button></div>
                    <hr />
                    {tache.id !== -1 && tache.instruction && (<Instructions instructions={tache.instruction} instructionsExpanded={instructionsExpanded} setInstructionsExpanded={setInstructionsExpanded} />)}
                    <Toolbar onSearchChange={onSearchChange} buttonsState={buttonsState} searchField={searchField} statuts={taches} setStatuts={setTaches} acheteur={acheteur} setAcheteur={setAcheteur} approuveFrancais={approuveFrancais} setApprouveFrancais={setApprouveFrancais} approuveAnglais={approuveAnglais} setApprouveAnglais={setApprouveAnglais} showFiltreTaches={tache.id === -1 && !searchMode} />
                    {!isLoading && !isFetching && (<div className='section-produits-liste k-flex-grow k-h-full k-max-h-full k-min-h-0 k-min-w-full k-max-w-0'>
                        {buttonsState.gridView && (
                            <>
                                <div className='vue-en-carte k-d-flex k-flex-column k-grid-cols-1 k-h-full k-max-h-full k-flex-grow'>
                                    <Grid data={filteredData.length > 0 ? orderBy(filteredData, sort) : []} sortable={{ allowUnsort: true, mode: 'single' }} resizable={true} sort={sort}
                                        onSortChange={(e: GridSortChangeEvent) => { if (e.sort.length === 0 || columns.find(f => f.field === e.sort[0].field)?.canInteract) setSort(e.sort); }}
                                        reorderable={true} skip={(page * pageSize) - pageSize} take={pageSize} total={total}
                                        onPageChange={(event) => {
                                            setPage((event.page.skip / event.page.take) + 1);
                                            setPageSize(event.page.take);
                                        }} className='k-flex-grow k-h-full k-w-full k-max-w-full k-overflow-auto'>
                                        {columns.map((c, i) => <GridColumn title={c.title} field={c.field} key={i} {...calculateWidth(c, i === columns.length - 1, filteredData)} cell={c.wrapper ? ActionsCellWrapper : c.cell}></GridColumn>)}
                                        <GridNoRecords>
                                            <p>Aucune donnée à afficher</p>
                                        </GridNoRecords>
                                    </Grid>
                                    <Pager className='k-listview-pager k-flex-grow-0' total={total} skip={(page * pageSize) - pageSize} take={pageSize} buttonCount={10} previousNext={true} pageSizes={pageSizes} onPageChange={(event: PageChangeEvent) => { setPage((event.skip / event.take) + 1); setPageSize(event.take); }} />
                                </div>
                            </>)
                        }
                        {buttonsState.listView && (<>
                            <div className='vue-en-carte k-d-flex k-flex-column k-grid-cols-1 k-h-full k-max-h-full k-flex-grow'>
                                {filteredData.length > 0 ? (
                                    <>
                                        <ListView data={listViewData} item={CustomListviewWrapper} className='k-h-full k-overflow-auto' />
                                        <Pager className='k-listview-pager k-flex-grow-0' total={total} skip={(page * pageSize) - pageSize} take={pageSize} buttonCount={10} previousNext={true} pageSizes={pageSizes} onPageChange={(event: PageChangeEvent) => { setPage((event.skip / event.take) + 1); setPageSize(event.take); }} /></>) : <h2 className="k-w-full k-text-center empty-grid">Aucune donnée à afficher!</h2>}
                            </div>
                        </>)}
                    </div>)}
                    {(isLoading || isFetching) && <LoaderContainer />}
                </div>
            </div>
        </div>
        {openModalNewTask && (<CustomModal title="Créer une nouvelle tâche à partir de cette liste de produits" body={<TaskForm setOpenModalNewTask={setOpenModalNewTask} four={fournisseur.four_cd} cat={categorie.cat_cd} dept={departement.dept_cd} div={division.div_num} tacheId={tache.id} ptr={searchProductValue} acheteur={acheteur} activite={activite.act_cd} approuveang={approuveAnglais} approuvefra={approuveFrancais} taches={taches} />} onClose={() => setOpenModalNewTask(false)} />)}
    </>;
}
